import React, { useRef, useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PROGRAMS } from "./siteData";

export const Programs = () => {
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const [maxX, setMaxX] = useState(0);

  useEffect(() => {
    const calc = () => {
      if (trackRef.current) {
        const overflow = trackRef.current.scrollWidth - window.innerWidth;
        setMaxX(overflow > 0 ? overflow : 0);
      }
    };
    calc();
    const t = setTimeout(calc, 400);
    const ro = trackRef.current ? new ResizeObserver(calc) : null;
    if (ro && trackRef.current) ro.observe(trackRef.current);
    window.addEventListener("resize", calc);
    return () => {
      clearTimeout(t);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", calc);
    };
  }, []);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });
  const x = useTransform(scrollYProgress, [0, 1], [0, -maxX]);
  const progress = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section
      id="programs"
      ref={sectionRef}
      data-testid="programs-section"
      style={{ height: `calc(100vh + ${maxX}px)` }}
      className="relative bg-zinc-950"
    >
      <div className="sticky top-0 h-screen flex flex-col justify-center overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full mb-8">
          <div className="flex items-end justify-between gap-4">
            <div>
              <span className="text-gold font-semibold uppercase tracking-[0.25em] text-sm">
                What We Offer
              </span>
              <h2 className="font-bebas text-5xl sm:text-6xl text-white mt-3 leading-none">
                Programs For Every Goal
              </h2>
            </div>
            <p className="hidden md:block text-zinc-500 text-sm uppercase tracking-widest">
              Scroll to explore →
            </p>
          </div>
        </div>

        <motion.div
          ref={trackRef}
          style={{ x }}
          className="flex gap-5 sm:gap-8 px-4 sm:px-6 lg:px-8 will-change-transform"
        >
          {PROGRAMS.map((p, i) => (
            <a
              key={p.name}
              href="#contact"
              data-testid={`program-card-${p.name.toLowerCase().replace(/ /g, "-")}`}
              className="group relative shrink-0 w-[78vw] sm:w-[440px] lg:w-[520px] h-[58vh] max-h-[540px] rounded-2xl overflow-hidden border border-white/10"
            >
              <img
                src={p.img}
                alt={p.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute top-5 left-5 w-9 h-9 rounded-full bg-black/50 border border-white/20 flex items-center justify-center text-gold text-sm font-bebas">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="absolute inset-0 p-7 flex flex-col justify-end">
                <div className="flex items-end justify-between">
                  <div>
                    <h3 className="font-bebas text-4xl text-white leading-none tracking-wide">
                      {p.name}
                    </h3>
                    <p className="text-zinc-300 text-sm mt-2 font-body max-w-[90%]">{p.desc}</p>
                  </div>
                  <span className="bg-gold text-black rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ArrowUpRight size={18} />
                  </span>
                </div>
              </div>
            </a>
          ))}
          <div className="shrink-0 w-[8vw]" aria-hidden="true" />
        </motion.div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full mt-8">
          <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden">
            <motion.div style={{ width: progress }} className="h-full bg-gold rounded-full" />
          </div>
        </div>
      </div>
    </section>
  );
};
